// src/components/gamification/XPToast.tsx
// Real-time XP feedback toast. Fires after every XP-earning action.
// Position: top-center. Stacks up to 3. Auto-dismisses after 1.5s.
// Uses Cinder Track v3.1 design system (cinder accent, JetBrains Mono numbers).

import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { onXPEvent, type XPProgressionResult, type LevelUpResult } from '../../services/xpEngine';

interface ToastItem {
  id: string;
  amount: number;
  source: string;
  timestamp: number;
}

interface XPToastProps {
  /** Called when a level-up is detected (parent renders full-screen animation) */
  onLevelUp?: (result: LevelUpResult) => void;
}

export default function XPToast({ onLevelUp }: XPToastProps) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const toastIdRef = useRef(0);

  const addToast = useCallback((amount: number, source: string) => {
    if (amount <= 0) return;
    const id = `xp-${++toastIdRef.current}`;
    setToasts(prev => {
      const next = [{ id, amount, source, timestamp: Date.now() }, ...prev];
      return next.slice(0, 3); // Max 3 visible
    });
  }, []);

  // Subscribe to XP events
  useEffect(() => {
    const unsub = onXPEvent((result: XPProgressionResult) => {
      if (result.xp.amount > 0) {
        addToast(result.xp.amount, result.xp.source);
      }
      if (result.levelUp && onLevelUp) {
        // Small delay so XP toast appears before level-up overlay
        setTimeout(() => onLevelUp(result.levelUp!), 800);
      }
    });
    return unsub;
  }, [addToast, onLevelUp]);

  // Auto-dismiss toasts after 2s
  useEffect(() => {
    if (toasts.length === 0) return;
    const timer = setTimeout(() => {
      setToasts(prev => prev.slice(0, -1)); // Remove oldest
    }, 2000);
    return () => clearTimeout(timer);
  }, [toasts]);

  return (
    <div style={{
      position: 'fixed', top: 24, left: '50%', transform: 'translateX(-50%)',
      zIndex: 90, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
      pointerEvents: 'none',
    }}>
      <AnimatePresence mode="popLayout">
        {toasts.map((toast, i) => (
          <motion.div
            key={toast.id}
            initial={{ opacity: 0, y: -20, scale: 0.9 }}
            animate={{ opacity: 1 - i * 0.25, y: 0, scale: 1 - i * 0.03 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: 'rgba(15,19,24,0.95)',
              border: '1px solid rgba(232,160,32,0.15)',
              borderRadius: 8,
              padding: '10px 16px',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
            }}
          >
            {/* Gold dot */}
            <div style={{
              width: 20, height: 20, borderRadius: '50%',
              background: 'rgba(232,160,32,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, color: 'var(--color-gold-LEGACY)', fontWeight: 700,
            }}>
              +
            </div>
            {/* XP amount */}
            <span className="font-mono" style={{
              fontSize: 16, fontWeight: 500,
              color: 'var(--color-gold-LEGACY)',
            }}>
              +{toast.amount} XP
            </span>
            {/* Source label */}
            <span style={{
              fontSize: 10, color: 'rgba(245,240,232,0.4)',
              fontFamily: 'Outfit, sans-serif', letterSpacing: '0.04em',
            }}>
              {toast.source}
            </span>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
